import { diff } from 'deep-object-diff';
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { createBrowserRouter, Outlet, Params, RouterProvider, useParams, } from "react-router-dom";

import { ResourceLoader } from './3d-viewer/resource-loader';
import { Viewer3DBabylon } from './3d-viewer/viewer-3d-babylon';
import { Viewer3DSync } from './3d-viewer/viewer-3d-babylon-sync';
import { Viewer3DContent } from './3d-viewer/viewer-3d-content';
import { basket3DActions } from './app/basket-3d-slice';
import { EMPTY_VIEWER_CONTENT, EXCLUDE_FROM_EXPORT } from './app/composition-to-viewer';
import { configuratorActions } from './app/configurator-slice';
import { EnvConfig, EnvConfigContext } from './app/context/environment-context';
import { AppLogin, Login } from './app/context/login';
import { devActions } from './app/dev-slice';
import { ErrorFallback } from "./app/error-boundaries/error-fallback";
import { EventEmitter, events, eventTypes } from "./app/event-emitter";
import { useAppDispatch } from "./app/hooks";
import { mainActions, mainInit } from "./app/main-slice";
import { store } from "./app/store";
import { MAIN_SCENE } from './app/types/composition';
import { viewerActions } from './app/viewer-slice';
import { DEFAULT_LANG } from "./defaults";
import { getUnitSystemByCountry } from './util/unit-conversion';
import { URLS } from './navigation';
import { Cart3DPage } from './pages/cart-3d-page/cart-3d-page';
import { ChooseCategoryPage } from './pages/choose-category-page';
import { ChooseFamilyPage } from './pages/choose-family-page';
import { ChooseLanguagePage } from './pages/choose-language-page';
import { ChooseSubFamilyPage } from "./pages/choose-subFamily-page";
import { ChooseVariantPage } from './pages/choose-variant-page';
import { ConfiguratorPage } from './pages/configurator-page';
import { LoggedDevTools } from './pages/dev-tools';
import { NotFoundPage } from './pages/not-found-page';
import { salesIntegrationLoader, SalesIntegrationPage } from './pages/sales-integration';

import "./index.css";

const LanguageUpdates = () => {
    const dispatch = useAppDispatch()
    const { langId } = useParams();
  
    useEffect(() => {
        if(langId) {
            dispatch(mainActions.setLang(langId ?? DEFAULT_LANG))
        }
    }, [ langId ]);
  
    return <Outlet />
  }

const routeChildren = [
    {
        path: URLS.root,
        element: <ChooseLanguagePage />,
        loader: async () => {
            store.dispatch(mainActions.enterPage('choose-language'));
            return true;
        }
    },
    {
        path: URLS.home,
        element: <ChooseCategoryPage />,
        loader: async () => {
            store.dispatch(mainActions.enterPage('choose-category'));
            return true;
        }
    },
    {
        path: URLS.cart3d,
        element: <Cart3DPage />,
        loader: async ({ params }: { params: Params<string>}) => {
            store.dispatch(mainActions.enterPage('cart3d'));
            const { cartId: cartIdStr } = params;
            const cartId = Number.parseInt(cartIdStr!);
            const sessionId = store.getState().main.sessionId;
            if(sessionId) {
                store.dispatch( basket3DActions.loadCart3D(cartId) );
            } else {
                store.dispatch( mainActions.addAfterLoginAction( { type: 'loadCart3D', cartId }))
            }
            return true;
        }
    },
    {
        path: URLS.category,
        element: <ChooseFamilyPage />,
        loader: async () => {
            store.dispatch(mainActions.enterPage('choose-family'));
            return true;
        }
    },
    {
        path: URLS.family,
        element: <ChooseVariantPage />,
        loader: async () => {
            store.dispatch(mainActions.enterPage('choose-variant'));
            return true;
        }
    },
    {
        path: URLS.subFamily,
        element: <ChooseSubFamilyPage />,
        loader: async () => {
            store.dispatch(mainActions.enterPage('choose-subfamily'));
            return true;
        }
    },
    {
        path: URLS.familyCfg,
        element: <ConfiguratorPage />,
        loader: async ({ params }: { params: Params<string>}) => {
            store.dispatch(mainActions.enterPage('configurator'));
            const { categoryId, familyId } = params;
            if(categoryId && familyId) {
                store.dispatch(configuratorActions.setConfiguratorPage({
                    category: categoryId,
                    family: familyId
                }));
            }
            return true;
        }
    },
    {
        path: URLS.variantCfg,
        element: <ConfiguratorPage />,
        loader: async ({ params }: { params: Params<string>}) => {
            store.dispatch(mainActions.enterPage('configurator'));
            const { categoryId, familyId, variantId } = params;
            if(categoryId && familyId) {
                store.dispatch(configuratorActions.setConfiguratorPage({
                    category: categoryId,
                    family: familyId, 
                    variant: variantId
                }));
            }
            return true;
        }
    },
    {
        path: '/:langId/dev-tools',
        element: <LoggedDevTools/>
    }
]

const router = createBrowserRouter(
    [
        {
            path: '/vk',
            element: <SalesIntegrationPage />,
            loader: async ({ request }: {  request: Request}) => {
                return salesIntegrationLoader(new URL(request.url).searchParams, store.dispatch);
            }
        },
        {
            path: '/app',
            element: <AppLogin appContext="app" allowAnonymous={false}/>,
            children: routeChildren.filter(r => r.path !== '/').map(r => ({
                ...r,
                path: `/app${r.path.replace('/:langId','')}`
            })),
            errorElement: <NotFoundPage />
        },
        {
            path: '/stapp',
            element: <AppLogin appContext="stapp" allowAnonymous={true}/>,
            children: routeChildren.filter(r => r.path !== '/').map(r => ({
                ...r,
                path: `/stapp${r.path.replace('/:langId','')}`
            })),
            errorElement: <NotFoundPage />
        },
        {
            path: '/',
            element: <Login><LanguageUpdates /></Login>,
            children: routeChildren,
            errorElement: <NotFoundPage />
        },        

    ],
    {
        basename: import.meta.env.BASE_URL,        
    },
)

const envConfig: EnvConfig = {
    thumbnailsPath: import.meta.env.VITE_ASSETS + '/thumbnails',
    thumbnailWidth: 120,
    thumbnailHeight: 120,
    modelsPath: '/models',
    materialsPath: '/materials',
    environmentPath: '/environment/dancing-hall'
    //environmentPath: '/environment/skybox'
    //environmentPath: '/environment/leadenhall-market'
}

// State management

store.dispatch(mainInit);
const initialSearchParams = new URLSearchParams(window.location.search);
if(initialSearchParams.has('DBG_ALTSAVE')) {
    const str = initialSearchParams.get('DBG_ALTSAVE');
    const value = str === 'false' ? false : true;
    store.dispatch(devActions.setShowAltSaveConfiguration(value));
}


// React UI
const uiRoot = document.getElementById("root")!;
ReactDOM.createRoot(uiRoot!).render(
    <React.StrictMode>
        <Provider store={store}>
            <EnvConfigContext.Provider value={envConfig}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <RouterProvider router={router} />
                </ErrorBoundary>
            </EnvConfigContext.Provider>
        </Provider>
    </React.StrictMode>,
)

// 3D Viewer
// Correct way to handle this
const viewerElement = document.getElementById('viewer');
if (!viewerElement) {
    throw new Error('Element with id "viewer" not found');
}

// Type-safe check for canvas element
if (!(viewerElement instanceof HTMLCanvasElement)) {
    throw new Error('Element with id "viewer" is not a canvas element');
}

// Now TypeScript knows viewerElement is HTMLCanvasElement
const viewerCanvas: HTMLCanvasElement = viewerElement;


const loader = new ResourceLoader(import.meta.env.VITE_ASSETS);

let viewer: Viewer3DBabylon|undefined = undefined;
let vsync: Viewer3DSync|undefined = undefined;
let eventEmitterSubscriptions: [ number, number ] = [ -1, -1 ];

// @ts-ignore
window.RESOURCE_LOADER = loader;

function resizeViewer() {
    const rect = viewerCanvas.getBoundingClientRect();
    viewerCanvas.width = rect.width;
    viewerCanvas.height = rect.height;
    if(viewer) {
        viewer.resize();
    }
}

window.addEventListener('resize', resizeViewer);

function triggerIfChanged<T>(callback: (value: T) => void): (value:T) => void {
    let previousValue: T | undefined;
    return (value: T) => {
        if (value !== previousValue) {
            callback(value);
            previousValue = value;
        }
    }
}

const triggerShowViewer = triggerIfChanged((showViewer: boolean) => {
    if (showViewer) {
        [ viewer, vsync, eventEmitterSubscriptions ] = createViewer();
        const state = store.getState();
        vsync.sync(state.viewer.content ?? EMPTY_VIEWER_CONTENT);
        viewer.showReferences(state.dev.showReferences);
        viewerCanvas.className = 'viewer3d'
        resizeViewer();
    } else {
        destroyViewer( viewer, eventEmitterSubscriptions);
        viewer = undefined;
        vsync = undefined;
        eventEmitterSubscriptions = [ -1, -1 ];
        viewerCanvas.className = 'viewer3d hidden';
    }
});



const triggerViewerContent = triggerIfChanged((viewerContent: Viewer3DContent|undefined) => {
    if(vsync) {
        if (viewerContent !== undefined) {
            vsync.sync(viewerContent);
        } else {
            vsync.sync(EMPTY_VIEWER_CONTENT)
        }    
    }
});

const triggerShowReferences = triggerIfChanged((showReferences: boolean) => {
    if(viewer) {
        viewer.showReferences(showReferences);
    }
});

store.subscribe(() => {

    const state = store.getState();

    let showViewer = (state.main.page === 'configurator' || state.main.page === 'cart3d' )
                     &&
                     // if not, it disturbs the choose language page if cart 3D URL
                     state.main.sessionId !== undefined;    

    triggerShowViewer(showViewer);
    //triggerAnimationPosition(state.configurator.animationPosition);
    triggerViewerContent(state.viewer.content);
    triggerShowReferences(state.dev.showReferences);
   
})



// @ts-ignore
window.debugPanel = () => store.dispatch(devActions.toggleDebugPanel());
// @ts-ignore
window.showInspector = () => {
    if(viewer) {
        viewer.showDebugInfo(true);
    }
}

EventEmitter.subscribe(eventTypes.VIEWER_EVENTS, ( event: string, data: any) => {
    switch (event) {
        case events.LAYOUT_RESULTS:
            const vState = store.getState().viewer;
            const old = {
                layout: vState.elementsLayout,
                dimensions: vState.elementDimensions
            };
            const difference = diff(old, data);
            if(Object.keys(difference).length > 0) {
                store.dispatch(viewerActions.setLayoutResults(data));
            }
            break;
    }
});


function sleep(ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
        setTimeout(resolve, ms);
    })
}

function createViewer(): [ Viewer3DBabylon, Viewer3DSync, [ number, number ] ] {

    const newViewer = new Viewer3DBabylon(viewerCanvas, {
        hideReferences: true,
        defaultScene: MAIN_SCENE,
        wireframe: false,
        broadcastEvents: true,
        offscreen: false
    });

    // @ts-ignore
    window.VIEWER = viewer;

    newViewer.resize();

    // Set unit system based on country
    const state = store.getState();
    const country = state.main.country;
    const unitSystem = getUnitSystemByCountry(country);
    newViewer.setUnitSystem(unitSystem);

    const newVsync = new Viewer3DSync(newViewer, loader);

    newVsync.addLoadingListener((l: boolean) => {
        store.dispatch(viewerActions.setLoading(l));
        if(l) {
            newViewer.stopRendering();
        } else {
            newViewer.startRendering();
        }
    });

    const viewControlsSubscription = EventEmitter.subscribe(eventTypes.VIEW_CONTROLS, ( event: string, data: any) => {

        switch (event) {
    
            case events.ZOOM_IN_EVENT:
                newViewer.zoom(1);
                break;
    
            case events.ZOOM_OUT_EVENT:
                newViewer.zoom(-1);
                break;
    
            case events.SET_ANIMATION_EVENT:
                newViewer.setAllAnimationPercentages(data.sceneId, data.percentage, false);
                break;
    
            case events.SET_CAMERA_EVENT:
                newViewer.setCamera('*CURRENT', data.camera, data.inmediate);
                break;   
                
            case events.SWITCH_CAMERA_EVENT:
                if(data.type === '2d') {
                    newViewer.use2DCamera(data.scene);
                } else {
                    newViewer.useOrbitCamera(data.scene);    
                }
                break;
    
            case events.FIT_CONTENT_EVENT:
                newViewer.fitContent(data, false);
                break;
                
            case events.SET_CURRENT_SCENE_EVENT:
                newViewer.setCurrentScene(data);
                break;
    
            case events.EXPORT_GLB_EVENT:
                newViewer.exportGLTF(MAIN_SCENE, EXCLUDE_FROM_EXPORT).then((blob: Blob) => {
                    const blobURL = URL.createObjectURL(blob);
                    store.dispatch(configuratorActions.setGlbURL(blobURL));
                });
                break;
    
            case events.AUGMENTED_REALITY_EVENT:
                viewerCanvas.style.display = 'none';
                uiRoot.style.display = 'none';
                newViewer.exportGLTF(MAIN_SCENE, EXCLUDE_FROM_EXPORT).then(async (blob: Blob) => {
                    const blobURL = URL.createObjectURL(blob);
                    document.body.insertAdjacentHTML('beforeend',`
                        <model-viewer style="width: 100vw; height: 100vh;" src="${blobURL}" ar shadow-intensity="1" camera-controls touch-action="pan-y"></model-viewer>
                    `)
                    let modelViewer = document.querySelector('model-viewer');
                    while(!modelViewer) {
                        await sleep(500);
                        modelViewer = document.querySelector('model-viewer');
                    }
                    // @ts-expect-error
                    while(!modelViewer.canActivateAR) {
                        await sleep(500);
                    }
                    // @ts-expect-error                
                    modelViewer.activateAR();
                })
                break;
    
            default:
                console.log('Unhandled event', event);
                break;
        }
    })

    const domEventsSubscription = EventEmitter.subscribe(eventTypes.DOM_EVENTS, ( event: string, data: any) => {
        switch (event) {
            case events.VIEW_OFFSET_EVENT:
                newViewer.setCameraOffset(data);
                break;
    
            default:
                console.log('Unhandled event', event);
                break;
        }
    });


    return [ newViewer, newVsync, [
        viewControlsSubscription,
        domEventsSubscription
    ] ];
}

function destroyViewer(viewer: Viewer3DBabylon|undefined, subscriptions: [ number, number ]) {

    EventEmitter.unsubscribe(eventTypes.VIEW_CONTROLS, subscriptions[0]);
    EventEmitter.unsubscribe(eventTypes.DOM_EVENTS, subscriptions[1]);

    viewer?.dispose();

}

// This may give a import cycle, but using the event emitter for this is overengineering
export async function takeSnapshot(scene: string, fitView: boolean, width: number, height: number): Promise<Blob> {

    if(viewer) {
        return viewer.snapshot(scene, fitView, width, height);
    } else {
        throw new Error('Viewer not active');
    }
    
}
